import jsep from 'jsep';

/**
 * Interpreter for a parsed formula AST.
 *
 * Values flow as numbers (NaN allowed — missing data degrades to NaN rather
 * than a hard failure), booleans (comparisons/ternaries), strings, and
 * caller-constructed context objects (member-access roots). `undefined` and
 * `null` reads propagate as NaN through arithmetic.
 *
 * Security: identifier, member, and function lookups are all gated on
 * `Object.hasOwn`, so prototype-inherited properties (`constructor`,
 * `__proto__`, etc.) are never reachable — including in lenient mode, which
 * returns `undefined` instead of throwing but never widens the lookup.
 */

export class FormulaEvaluationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FormulaEvaluationError';
  }
}

export type FormulaFunction = (...args: unknown[]) => unknown;

export interface FormulaScopeOptions {
  /**
   * When true, member access on a missing/null/non-object base resolves to
   * `undefined` (→ NaN through arithmetic) instead of throwing. Suited to
   * loosely-shaped data like template records.
   */
  lenientMemberAccess?: boolean;
  /**
   * When true, numeric strings are `Number()`-coerced in arithmetic and
   * comparisons (empty/blank strings become NaN). Matches how the template
   * formatting pipeline treats numeric fields.
   */
  coerceNumericStrings?: boolean;
}

export interface FormulaScope {
  /**
   * identifier name → value. Objects are allowed as member-access roots;
   * they must be plain, caller-constructed objects.
   */
  identifiers: Record<string, unknown>;
  functions: Record<string, FormulaFunction>;
  options?: FormulaScopeOptions;
}

const asNumber = (
  value: unknown,
  context: string,
  options?: FormulaScopeOptions,
): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (value === undefined || value === null) {
    return NaN;
  }
  if (options?.coerceNumericStrings && typeof value === 'string') {
    return value.trim() === '' ? NaN : Number(value);
  }
  throw new FormulaEvaluationError(
    `Expected a number for ${context}, received ${typeof value}`,
  );
};

const isTruthy = (value: unknown): boolean => Boolean(value);

const evaluateBinary = (
  operator: string,
  left: unknown,
  right: unknown,
  options?: FormulaScopeOptions,
): unknown => {
  const context = `"${operator}"`;
  switch (operator) {
    case '+':
      return (
        asNumber(left, context, options) + asNumber(right, context, options)
      );
    case '-':
      return (
        asNumber(left, context, options) - asNumber(right, context, options)
      );
    case '*':
      return (
        asNumber(left, context, options) * asNumber(right, context, options)
      );
    case '/':
      return (
        asNumber(left, context, options) / asNumber(right, context, options)
      );
    case '%':
      return (
        asNumber(left, context, options) % asNumber(right, context, options)
      );
    case '**':
      return (
        asNumber(left, context, options) ** asNumber(right, context, options)
      );
    case '<':
      return (
        asNumber(left, context, options) < asNumber(right, context, options)
      );
    case '<=':
      return (
        asNumber(left, context, options) <= asNumber(right, context, options)
      );
    case '>':
      return (
        asNumber(left, context, options) > asNumber(right, context, options)
      );
    case '>=':
      return (
        asNumber(left, context, options) >= asNumber(right, context, options)
      );
    case '==':
    case '===':
      return left === right;
    case '!=':
    case '!==':
      return left !== right;
    default:
      throw new FormulaEvaluationError(
        `Operator "${operator}" is not supported`,
      );
  }
};

export const evaluateFormula = (
  node: jsep.Expression,
  scope: FormulaScope,
): unknown => {
  switch (node.type) {
    case 'Literal':
      return (node as jsep.Literal).value;

    case 'Identifier': {
      const { name } = node as jsep.Identifier;
      if (!Object.hasOwn(scope.identifiers, name)) {
        throw new FormulaEvaluationError(`Unknown identifier "${name}"`);
      }
      return scope.identifiers[name];
    }

    case 'MemberExpression': {
      const member = node as jsep.MemberExpression;
      const objectValue = evaluateFormula(member.object, scope);
      const propertyName = (member.property as jsep.Identifier).name;
      if (
        objectValue === null ||
        typeof objectValue !== 'object' ||
        !Object.hasOwn(objectValue, propertyName)
      ) {
        if (scope.options?.lenientMemberAccess) {
          return undefined;
        }
        throw new FormulaEvaluationError(`Unknown property "${propertyName}"`);
      }
      return (objectValue as Record<string, unknown>)[propertyName];
    }

    case 'CallExpression': {
      const call = node as jsep.CallExpression;
      const calleeName = (call.callee as jsep.Identifier).name;
      if (!Object.hasOwn(scope.functions, calleeName)) {
        throw new FormulaEvaluationError(`Unknown function "${calleeName}"`);
      }
      const args = call.arguments.map((arg) => evaluateFormula(arg, scope));
      return scope.functions[calleeName](...args);
    }

    case 'UnaryExpression': {
      const unary = node as jsep.UnaryExpression;
      const argument = evaluateFormula(unary.argument, scope);
      switch (unary.operator) {
        case '-':
          return -asNumber(argument, 'unary "-"', scope.options);
        case '+':
          return asNumber(argument, 'unary "+"', scope.options);
        case '!':
          return !isTruthy(argument);
        default:
          throw new FormulaEvaluationError(
            `Operator "${unary.operator}" is not supported`,
          );
      }
    }

    case 'BinaryExpression': {
      const binary = node as jsep.BinaryExpression;
      // Short-circuit logical operators without evaluating the other side
      if (binary.operator === '&&') {
        const left = evaluateFormula(binary.left, scope);
        return isTruthy(left) ? evaluateFormula(binary.right, scope) : left;
      }
      if (binary.operator === '||') {
        const left = evaluateFormula(binary.left, scope);
        return isTruthy(left) ? left : evaluateFormula(binary.right, scope);
      }
      return evaluateBinary(
        binary.operator,
        evaluateFormula(binary.left, scope),
        evaluateFormula(binary.right, scope),
        scope.options,
      );
    }

    case 'ConditionalExpression': {
      const conditional = node as jsep.ConditionalExpression;
      return isTruthy(evaluateFormula(conditional.test, scope))
        ? evaluateFormula(conditional.consequent, scope)
        : evaluateFormula(conditional.alternate, scope);
    }

    default:
      throw new FormulaEvaluationError(`Unsupported syntax: ${node.type}`);
  }
};
